export type AccountSummaryBadgeVariant = 'default' | 'supplementary'

export type AccountSummaryPaymentMethodItem = {
  id: string
  lastFour: string
  bankName: string
  badge: AccountSummaryBadgeVariant
}

export type AccountSummarySavedCardItem = {
  id: string
  lastFour: string
  brand: string
  badge: AccountSummaryBadgeVariant
}

export type PatientAccountSummaryData = {
  balances: {
    outstanding: number
    insurance: number
    patient: number
    family: number
    discountApplied: number
    renewalMonth: string
  }
  paymentMethods: AccountSummaryPaymentMethodItem[]
  savedCards: AccountSummarySavedCardItem[]
}

export type ProfilePaymentCardChip = {
  id: string
  lastFour: string
  brand: string
}

export type ProfilePaymentBankChip = {
  id: string
  lastFour: string
  bankName: string
}

export type ProfilePaymentMethodsSummary = {
  cards: ProfilePaymentCardChip[]
  banks: ProfilePaymentBankChip[]
}

export type DemoAccountARAnalytics = {
  balance: number
  total: number
  percentage: number
}

/** AR metrics for Patient Profile Account summary demo (John Mark K. Doe). */
export const DEMO_ACCOUNT_AR = {
  patient: {
    balance: 300,
    total: 3000,
    percentage: 10,
  } satisfies DemoAccountARAnalytics,
  insurance: {
    balance: 60,
    total: 600,
    percentage: 10,
  } satisfies DemoAccountARAnalytics,
  agingPeriods: [
    {
      label: '0-30 days',
      topBarValue: 35,
      bottomBarValue: 20,
      patientAmount: 105,
      insuranceAmount: 12,
    },
    {
      label: '31-60 days',
      topBarValue: 45,
      bottomBarValue: 30,
      patientAmount: 90,
      insuranceAmount: 18,
    },
    {
      label: '61-90 days',
      topBarValue: 25,
      bottomBarValue: 40,
      patientAmount: 60,
      insuranceAmount: 20,
    },
    {
      label: '90+ days',
      topBarValue: 15,
      bottomBarValue: 10,
      patientAmount: 45,
      insuranceAmount: 10,
    },
  ],
  arAgingTotal: 360,
}

export const DEMO_ACCOUNT_SUMMARY: PatientAccountSummaryData = {
  balances: {
    outstanding: 300,
    insurance: 60,
    patient: 300,
    family: 1400,
    discountApplied: 500,
    renewalMonth: 'October',
  },
  paymentMethods: [
    {
      id: 'pm-bank-1',
      lastFour: '3944',
      bankName: 'Bank of America',
      badge: 'supplementary',
    },
  ],
  savedCards: [
    {
      id: 'card-1',
      lastFour: '4242',
      brand: 'Visa',
      badge: 'default',
    },
    {
      id: 'card-2',
      lastFour: '4242',
      brand: 'MasterCard',
      badge: 'supplementary',
    },
  ],
}

/** Profile tab payment summary demo (Figma: Visa 8842, MasterCard 4553, BoA 3944). */
export const DEMO_PROFILE_PAYMENT_METHODS_SUMMARY: ProfilePaymentMethodsSummary =
  {
    cards: [
      { id: 'profile-card-1', lastFour: '8842', brand: 'Visa' },
      { id: 'profile-card-2', lastFour: '4553', brand: 'MasterCard' },
    ],
    banks: [
      {
        id: 'profile-bank-1',
        lastFour: '3944',
        bankName: 'Bank of America',
      },
    ],
  }

/** Placeholder values so account summary UI renders on real patients before API wiring. */
export const EMPTY_ACCOUNT_SUMMARY: PatientAccountSummaryData = {
  balances: {
    outstanding: 0,
    insurance: 0,
    patient: 0,
    family: 0,
    discountApplied: 0,
    renewalMonth: 'None',
  },
  paymentMethods: [],
  savedCards: [],
}
